import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { UserPlus, Mail, Shield, Eye, Users, Share2 } from 'lucide-react';
import { mockFamilyMembers } from '../mock';
import { toast } from '../hooks/use-toast';
import { Badge } from '../components/ui/badge';

const CareTeam = () => {
  const [familyMembers, setFamilyMembers] = useState(mockFamilyMembers);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newMember, setNewMember] = useState({
    name: '',
    relationship: 'Family',
    email: '',
    access_level: 'View Only'
  });

  const handleAddMember = () => {
    if (!newMember.name || !newMember.email) {
      toast({ title: "Error", description: "Please fill in all required fields", variant: "destructive" });
      return;
    }

    const member = {
      member_id: `fam_${Date.now()}`,
      name: newMember.name,
      relationship: newMember.relationship,
      email: newMember.email,
      access_level: newMember.access_level,
      avatar: newMember.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    };

    setFamilyMembers([...familyMembers, member]);
    setDialogOpen(false);
    setNewMember({
      name: '',
      relationship: 'Family',
      email: '',
      access_level: 'View Only'
    });

    toast({ title: "Invitation Sent!", description: `An email invitation has been sent to ${newMember.email}` });
  };

  const handleRemoveMember = (memberId) => {
    setFamilyMembers(familyMembers.filter(m => m.member_id !== memberId));
    toast({ title: "Member Removed", description: "Care team member has been removed" });
  };

  const accessLevelColors = {
    'Full': 'bg-emerald-100 text-emerald-700',
    'View Only': 'bg-blue-100 text-blue-700',
    'Emergency Only': 'bg-orange-100 text-orange-700'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 rounded-2xl p-8 text-white shadow-lg">
        <h2 className="text-3xl font-bold mb-2">Care Team & Sharing</h2>
        <p className="text-teal-100 text-lg">Share your health data with family and healthcare providers</p>
      </div>

      {/* Info Cards */}
      <div className="grid md:grid-cols-3 gap-6">
        <Card className="shadow-md border-0">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Team Members</CardTitle>
            <Users className="w-5 h-5 text-teal-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">{familyMembers.length}</div>
            <p className="text-sm text-gray-600 mt-1">Connected</p>
          </CardContent>
        </Card>

        <Card className="shadow-md border-0">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Data Shared</CardTitle>
            <Share2 className="w-5 h-5 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">4</div>
            <p className="text-sm text-gray-600 mt-1">Types</p>
          </CardContent>
        </Card>

        <Card className="shadow-md border-0">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Access Level</CardTitle>
            <Shield className="w-5 h-5 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">Secure</div>
            <p className="text-sm text-gray-600 mt-1">Encrypted</p>
          </CardContent>
        </Card>
      </div>

      {/* Add Member */}
      <Card className="shadow-md border-0">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Your Care Team</CardTitle>
              <CardDescription>Manage who can access your health data</CardDescription>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Member
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Invite Care Team Member</DialogTitle>
                  <DialogDescription>
                    Share your health data with family or healthcare providers
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      placeholder="John Doe"
                      value={newMember.name}
                      onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@example.com"
                      value={newMember.email}
                      onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="relationship">Relationship</Label>
                    <Select
                      value={newMember.relationship}
                      onValueChange={(value) => setNewMember({ ...newMember, relationship: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Family">Family Member</SelectItem>
                        <SelectItem value="Spouse">Spouse/Partner</SelectItem>
                        <SelectItem value="Doctor">Doctor</SelectItem>
                        <SelectItem value="Endocrinologist">Endocrinologist</SelectItem>
                        <SelectItem value="Nurse">Nurse/Educator</SelectItem>
                        <SelectItem value="Caregiver">Caregiver</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="access">Access Level</Label>
                    <Select
                      value={newMember.access_level}
                      onValueChange={(value) => setNewMember({ ...newMember, access_level: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="View Only">View Only</SelectItem>
                        <SelectItem value="Full">Full Access</SelectItem>
                        <SelectItem value="Emergency Only">Emergency Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs text-blue-800">
                      <strong>View Only:</strong> Can see your data but cannot make changes<br/>
                      <strong>Full Access:</strong> Can view and add data on your behalf<br/>
                      <strong>Emergency Only:</strong> Access only during emergencies
                    </p>
                  </div>
                  <Button onClick={handleAddMember} className="w-full bg-gradient-to-r from-teal-500 to-cyan-500">
                    Send Invitation
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {familyMembers.map((member) => (
              <div
                key={member.member_id}
                className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white rounded-lg border border-gray-200 hover:shadow-md transition-all"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-full flex items-center justify-center text-white font-semibold">
                    {member.avatar}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{member.name}</p>
                    <p className="text-sm text-gray-600">{member.relationship}</p>
                    <p className="text-xs text-gray-500">{member.email}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Badge className={accessLevelColors[member.access_level]}>
                    {member.access_level === 'View Only' && <Eye className="w-3 h-3 mr-1" />}
                    {member.access_level === 'Full' && <Shield className="w-3 h-3 mr-1" />}
                    {member.access_level}
                  </Badge>
                  <Button
                    onClick={() => handleRemoveMember(member.member_id)}
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Privacy Info */}
      <Card className="shadow-md border-0 bg-gradient-to-br from-gray-50 to-blue-50">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Shield className="w-5 h-5 text-blue-600" />
            <span>Privacy & Security</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            <h4 className="font-semibold text-gray-900">Your data is protected</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• All shared data is encrypted end-to-end</li>
              <li>• You can revoke access at any time</li>
              <li>• Team members only see what you choose to share</li>
              <li>• Activity logs track all data access</li>
              <li>• HIPAA compliant sharing protocols</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CareTeam;
